import { useState, useEffect, useMemo } from 'react';
import { useAuthStore } from '../../store/authStore';
import { api } from '../../api/client';
import { toast } from 'sonner';
import { FileText, Check, X, Clock, AlertCircle, Calendar, User, Download, Image as ImageIcon } from 'lucide-react';
import { useHojeReferencia } from '../../api/hooks/useSimClock';
import {
  filterJustificativasPorEscopo,
  resolverStatusExibicao,
  type EscopoJustificativa,
} from './justificativaEscopo';
import './justificativas-gestao.css';

interface Justificativa {
  id: string;
  dataFalta: string;
  motivo: string;
  anexoUrl?: string | null;
  status: 'PENDENTE' | 'ACEITA' | 'RECUSADA';
  criadoEm: string;
  aluno: {
    id: string;
    nome: string;
    matricula: string;
    turma?: { id: string; nome: string; serie: string } | null;
  };
  revisadoPor?: { id: string; nome: string; email?: string | null } | null;
  revisadoEm?: string | null;
}

function parseMotivo(motivoStr: string) {
  const match = motivoStr.match(/^\[(.*?)\](.*)$/);
  if (match) {
    const cat = match[1];
    const text = match[2];
    let label = cat;
    let color = '#64748b';
    let bg = '#f1f5f9';

    if (cat === 'SAUDE') { label = 'Saúde'; color = '#ef4444'; bg = '#fef2f2'; }
    else if (cat === 'CONSULTA') { label = 'Consulta'; color = '#3b82f6'; bg = '#eff6ff'; }
    else if (cat === 'FAMILIA') { label = 'Familiar'; color = '#f59e0b'; bg = '#fffbeb'; }
    else if (cat === 'OUTRO') { label = 'Outros'; color = '#8b5cf6'; bg = '#f5f3ff'; }

    return { label, text: text.trim(), color, bg };
  }
  return { label: 'Geral', text: motivoStr, color: '#64748b', bg: '#f1f5f9' };
}

export function JustificativasGestao() {
  const escolaId = useAuthStore((s) => s.escolaId);
  const { hojeStr } = useHojeReferencia();
  const [justificativas, setJustificativas] = useState<Justificativa[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedJustificativa, setSelectedJustificativa] = useState<Justificativa | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [escopo, setEscopo] = useState<EscopoJustificativa>('ATIVAS');
  const [filtroStatus, setFiltroStatus] = useState<string>('TODAS');

  const fetchJustificativas = async () => {
    if (!escolaId) return;
    setLoading(true);
    try {
      const res = await api.get(`/escolas/${escolaId}/presencas-diarias/justificativas`);
      setJustificativas(res.data || []);
      setSelectedJustificativa((prev) => {
        if (!prev) return null;
        return (res.data || []).find((j: Justificativa) => j.id === prev.id) || null;
      });
    } catch {
      toast.error('Erro ao carregar justificativas.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJustificativas();
  }, [escolaId]);

  const handleRevisar = async (id: string, status: 'ACEITA' | 'RECUSADA') => {
    if (!escolaId) return;
    setActionLoading(true);
    try {
      await api.patch(`/escolas/${escolaId}/presencas-diarias/justificativas/${id}/revisar`, { status });
      toast.success(
        status === 'ACEITA'
          ? 'Justificativa aceita e ausência abonada no diário.'
          : 'Justificativa recusada. Responsável notificado.',
      );
      await fetchJustificativas();
    } catch {
      toast.error('Erro ao atualizar status da justificativa.');
    } finally {
      setActionLoading(false);
    }
  };

  const filteredList = useMemo(() => {
    const porEscopo = filterJustificativasPorEscopo(justificativas, escopo, hojeStr);
    if (filtroStatus === 'TODAS') return porEscopo;
    return porEscopo.filter((j) => j.status === filtroStatus);
  }, [justificativas, escopo, filtroStatus, hojeStr]);

  useEffect(() => {
    if (
      selectedJustificativa &&
      !filteredList.some((j) => j.id === selectedJustificativa.id)
    ) {
      setSelectedJustificativa(null);
    }
  }, [filteredList, selectedJustificativa]);

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '—';
    try {
      const dtStr = dateStr.includes('T') ? dateStr : `${dateStr}T12:00:00`;
      const date = new Date(dtStr);
      if (isNaN(date.getTime())) return dateStr;
      return date.toLocaleDateString('pt-BR');
    } catch {
      return dateStr;
    }
  };

  const formatDateTime = (dateStr: string) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleString('pt-BR');
    } catch {
      return dateStr;
    }
  };

  const ativasCount = filterJustificativasPorEscopo(justificativas, 'ATIVAS', hojeStr).length;
  const historicoCount = filterJustificativasPorEscopo(justificativas, 'HISTORICO', hojeStr).length;
  const pendentes = justificativas.filter((j) => j.status === 'PENDENTE').length;
  const aceitas = justificativas.filter((j) => j.status === 'ACEITA').length;

  return (
    <div className="admin-page ac-justif-page">
      <div className="ac-justif-header">
        <div>
          <h1>Justificativas de Falta</h1>
          <p>
            Fila do dia e histórico. Após a data da falta, o registro sai da fila (Ativas) e fica no histórico —
            inclusive na ficha do aluno.
          </p>
        </div>
        <div className="ac-justif-counters">
          <div className="ac-justif-counter is-pendente">
            <strong>{pendentes}</strong>
            <span>Pendentes</span>
          </div>
          <div className="ac-justif-counter is-aceita">
            <strong>{aceitas}</strong>
            <span>Aceitas</span>
          </div>
        </div>
      </div>

      <div className="ac-justif-escopo">
        {(
          [
            { key: 'ATIVAS' as const, label: `Ativas (${ativasCount})` },
            { key: 'HISTORICO' as const, label: `Histórico (${historicoCount})` },
            { key: 'TODAS' as const, label: `Todas (${justificativas.length})` },
          ] as const
        ).map((opt) => (
          <button
            key={opt.key}
            type="button"
            onClick={() => setEscopo(opt.key)}
            className={escopo === opt.key ? 'is-active' : ''}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <div className="ac-justif-filters">
        {['TODAS', 'PENDENTE', 'ACEITA', 'RECUSADA'].map((status) => (
          <button
            key={status}
            type="button"
            onClick={() => setFiltroStatus(status)}
            className={filtroStatus === status ? 'is-active' : ''}
          >
            {status === 'TODAS' ? 'Todas' : status === 'PENDENTE' ? 'Pendentes' : status === 'ACEITA' ? 'Aceitas' : 'Recusadas'}
          </button>
        ))}
      </div>

      <div className="ac-justif-grid">
        <section className="ac-justif-list-panel">
          <header>
            {escopo === 'HISTORICO' ? 'Histórico' : escopo === 'ATIVAS' ? 'Fila ativa' : 'Solicitações'} ({filteredList.length})
          </header>
          <div className="ac-justif-list">
            {loading ? (
              <div className="ac-justif-empty">Carregando...</div>
            ) : filteredList.length === 0 ? (
              <div className="ac-justif-empty">
                <FileText size={32} />
                <span>Nenhuma justificativa encontrada neste filtro.</span>
              </div>
            ) : (
              filteredList.map((j) => {
                const parsed = parseMotivo(j.motivo);
                const statusUi = resolverStatusExibicao(j, hojeStr);
                const isSelected = selectedJustificativa?.id === j.id;
                return (
                  <button
                    type="button"
                    key={j.id}
                    className={`ac-justif-item status-${statusUi.toLowerCase()}${isSelected ? ' is-selected' : ''}`}
                    onClick={() => setSelectedJustificativa(j)}
                  >
                    <div className="ac-justif-item-top">
                      <span className="ac-justif-cat" style={{ color: parsed.color, background: parsed.bg }}>
                        {parsed.label}
                      </span>
                      <span style={{
                        fontSize: '0.65rem',
                        fontWeight: 700,
                        color: statusUi === 'VENCIDA' ? '#64748b' : statusUi === 'PENDENTE' ? '#d97706' : statusUi === 'ACEITA' ? '#16a34a' : '#dc2626',
                      }}>
                        {statusUi === 'VENCIDA' ? 'Vencida' : statusUi === 'PENDENTE' ? 'Em análise' : statusUi === 'ACEITA' ? 'Aceita' : 'Recusada'}
                        {' · '}Falta: {formatDate(j.dataFalta)}
                      </span>
                    </div>
                    <h4>{j.aluno.nome}</h4>
                    <p>{j.aluno.turma?.nome ? `${j.aluno.turma.nome} · ` : ''}{parsed.text}</p>
                    <div className="ac-justif-item-foot">
                      <span>
                        {statusUi === 'VENCIDA'
                          ? 'Prazo passou sem decisão'
                          : j.status !== 'PENDENTE' && j.revisadoPor?.nome
                            ? `${j.status === 'ACEITA' ? 'Aprov.' : 'Recus.'}: ${j.revisadoPor.nome}`
                            : `Enviado: ${formatDate(j.criadoEm)}`}
                      </span>
                      {j.anexoUrl && (
                        <span className="ac-justif-anexo">
                          <ImageIcon size={10} /> Com atestado
                        </span>
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </section>

        <section className="ac-justif-detail-panel">
          {selectedJustificativa ? (
            <>
              <header className="ac-justif-detail-head">
                <div>
                  <h3>Detalhamento do Registro</h3>
                  <span>Abono afeta chamada do dia e notifica o responsável</span>
                </div>
                <span className={`ac-justif-status-badge status-${resolverStatusExibicao(selectedJustificativa, hojeStr).toLowerCase()}`}>
                  {(() => {
                    const ui = resolverStatusExibicao(selectedJustificativa, hojeStr);
                    if (ui === 'VENCIDA') return 'Vencida (sem decisão no prazo)';
                    if (ui === 'PENDENTE') return 'Pendente de Análise';
                    if (ui === 'ACEITA') return 'Aprovada / Abonada';
                    return 'Recusada';
                  })()}
                </span>
              </header>

              <div className="ac-justif-detail-body">
                <div className="ac-justif-aluno-card">
                  <div className="ac-justif-avatar">
                    <User size={22} />
                  </div>
                  <div>
                    <h4>{selectedJustificativa.aluno.nome}</h4>
                    <p>
                      Matrícula: {selectedJustificativa.aluno.matricula || '—'} · Turma:{' '}
                      {selectedJustificativa.aluno.turma?.nome || 'Não vinculada'}
                    </p>
                  </div>
                </div>

                <div className="ac-justif-meta-grid">
                  <div>
                    <div className="ac-justif-meta-label">
                      <Calendar size={12} /> Data da Falta
                    </div>
                    <strong>{formatDate(selectedJustificativa.dataFalta)}</strong>
                  </div>
                  <div>
                    <div className="ac-justif-meta-label">
                      <Clock size={12} /> Data de Envio
                    </div>
                    <strong>{formatDateTime(selectedJustificativa.criadoEm)}</strong>
                  </div>
                </div>

                <div>
                  <h4 className="ac-justif-section-title">
                    {parseMotivo(selectedJustificativa.motivo).label} · Motivo
                  </h4>
                  <p className="ac-justif-motivo">{parseMotivo(selectedJustificativa.motivo).text}</p>
                </div>

                <div>
                  <h4 className="ac-justif-section-title">Atestado / Documento Anexo</h4>
                  {selectedJustificativa.anexoUrl ? (
                    <div className="ac-justif-anexo-box">
                      {selectedJustificativa.anexoUrl.startsWith('data:image/') ||
                      selectedJustificativa.anexoUrl.startsWith('image/') ? (
                        <div className="ac-justif-anexo-preview">
                          <img src={selectedJustificativa.anexoUrl} alt="Atestado" />
                          <a
                            href={selectedJustificativa.anexoUrl}
                            download={`atestado-${selectedJustificativa.aluno.nome}.png`}
                          >
                            <Download size={12} /> Baixar Atestado
                          </a>
                        </div>
                      ) : (
                        <div className="ac-justif-anexo-file">
                          <FileText size={20} />
                          <div>
                            <strong>Documento anexado</strong>
                            <span>Enviado pelo responsável</span>
                          </div>
                          <a
                            href={selectedJustificativa.anexoUrl}
                            download={`atestado-${selectedJustificativa.aluno.nome}.pdf`}
                          >
                            <Download size={14} /> Download
                          </a>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="ac-justif-empty soft">
                      <AlertCircle size={24} />
                      <span>Nenhum atestado ou arquivo anexo foi enviado.</span>
                    </div>
                  )}
                </div>

                {selectedJustificativa.status !== 'PENDENTE' && (
                  <div className="ac-justif-audit">
                    {selectedJustificativa.revisadoPor ? (
                      <>
                        {selectedJustificativa.status === 'ACEITA' ? 'Aprovado' : 'Recusado'} por{' '}
                        <strong>{selectedJustificativa.revisadoPor.nome}</strong>
                        {selectedJustificativa.revisadoPor.email
                          ? ` (${selectedJustificativa.revisadoPor.email})`
                          : ''}
                        {' '}em {formatDateTime(selectedJustificativa.revisadoEm || '')}.
                      </>
                    ) : (
                      <>
                        Status alterado em {formatDateTime(selectedJustificativa.revisadoEm || '')}
                        {' '}(revisor não registrado — registro antigo/demo).
                      </>
                    )}
                  </div>
                )}
              </div>

              {selectedJustificativa.status === 'PENDENTE' && (
                <footer className="ac-justif-actions">
                  <button
                    type="button"
                    disabled={actionLoading}
                    className="is-recusar"
                    onClick={() => handleRevisar(selectedJustificativa.id, 'RECUSADA')}
                  >
                    <X size={15} /> Recusar
                  </button>
                  <button
                    type="button"
                    disabled={actionLoading}
                    className="is-aprovar"
                    onClick={() => handleRevisar(selectedJustificativa.id, 'ACEITA')}
                  >
                    <Check size={15} /> Aprovar / Abonar
                  </button>
                </footer>
              )}
            </>
          ) : (
            <div className="ac-justif-empty center">
              <FileText size={48} />
              <h3>Selecione um registro</h3>
              <p>Escolha uma justificativa na lista para ver detalhes, abonar a falta e notificar o responsável.</p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
